import {
  faCheckCircle,
  faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useI18n } from 'hooks/useI18n';
import { ReactNode } from 'react';
import {
  StyledBottomAction,
  StyledFormGroup,
  StyledFormGroupContent,
  StyledFormGroupError,
  StyledFormGroupField,
  StyledFormGroupLabel,
  StyledFormGroupLabelExtra,
  StyledFormGroupLabelWrapper,
  StyledFormGroupSpinnerSlot,
  StyledFormGroupSuccess,
  StyledHint
} from './FormGroup.styled';
import { HelpTooltip } from './HelpTooltip/HelpTooltip';

export type FormGroupProps = {
  label?: React.ReactNode;
  labelExtra?: React.ReactNode;
  children: React.ReactNode;
  bottomAction?: React.ReactNode;
  hint?: React.ReactNode;
  error?: string;
  success?: string;
  forId?: string;
  className?: string;
  isLabelMuted?: boolean;
  style?: React.CSSProperties;
  inline?: boolean;
  helpContent?: React.ReactNode;
  isLabelHidden?: boolean;
  append?: ReactNode;
  spinnerSlot?: ReactNode;
};

const FormGroup = ({
  label,
  children,
  error,
  forId,
  className,
  labelExtra,
  bottomAction,
  isLabelMuted = false,
  inline = false,
  style,
  success,
  helpContent,
  isLabelHidden = false,
  hint,
  append,
  spinnerSlot
}: FormGroupProps) => {
  const { t } = useI18n();

  return (
    <StyledFormGroup className={className} style={style} inline={inline}>
      {(label || labelExtra) && (
        <StyledFormGroupLabelWrapper
          className={`${isLabelHidden ? 'sr-only' : ''}`}
        >
          {label && (
            <StyledFormGroupLabel
              htmlFor={forId}
              className={`${isLabelMuted ? 'muted' : ''} ${
                isLabelHidden ? 'sr-only' : ''
              }`}
            >
              {label}
            </StyledFormGroupLabel>
          )}

          {helpContent && <HelpTooltip message={helpContent} />}

          {labelExtra && (
            <StyledFormGroupLabelExtra>{labelExtra}</StyledFormGroupLabelExtra>
          )}
        </StyledFormGroupLabelWrapper>
      )}

      <StyledFormGroupField withAppend={!!append}>
        <StyledFormGroupContent>{children}</StyledFormGroupContent>
        {append}
      </StyledFormGroupField>

      {hint && <StyledHint>{hint}</StyledHint>}

      {bottomAction && <StyledBottomAction>{bottomAction}</StyledBottomAction>}

      {error && (
        <StyledFormGroupError>
          <FontAwesomeIcon icon={faTimesCircle} />
          {t(error)}
        </StyledFormGroupError>
      )}

      {success && (
        <StyledFormGroupSuccess>
          <FontAwesomeIcon icon={faCheckCircle} />
          {success}
        </StyledFormGroupSuccess>
      )}

      {spinnerSlot && (
        <StyledFormGroupSpinnerSlot>{spinnerSlot}</StyledFormGroupSpinnerSlot>
      )}
    </StyledFormGroup>
  );
};

export default FormGroup;
